import { z } from "zod";

export const trainingExerciseTypeSchema = z.enum([
  "POWER",
  "CARDIO",
  "STRETCHING",
  "MOBILIZATION",
  "CALISTHENICS",
  "REHABILITATION",
]);

export const trainingExerciseBodyPartSchema = z.enum([
  "LEGS",
  "BACK",
  "CHEST",
  "SHOULDERS",
  "ARMS",
  "GENERAL_DEVELOPMENT",
  "ABS",
  "GLUTS",
  "FOREARMS",
  "CORE",
  "BICEPS",
  "TRICEPS",
]);

const numericRangeSchema = z.union([
  z.number().nonnegative(),
  z
    .tuple([z.number().nonnegative(), z.number().nonnegative()])
    .refine(([minimum, maximum]) => maximum >= minimum, "Koniec zakresu nie może być mniejszy od początku"),
]);

export const trainingProgramDraftExerciseSchema = z.object({
  requestedName: z.string().min(1).max(200),
  // Nie .uuid() — baza ma legacy ID z Firebase (20-znakowe push ID); istnienie
  // weryfikuje proposeTrainingProgramTool zapytaniem do bazy.
  exerciseId: z.string().min(1).max(64).nullable(),
  resolutionStatus: z.enum(["matched", "unresolved", "ambiguous"]),
  numberOfSeries: z.number().int().min(1).max(20),
  repeats: numericRangeSchema.nullable().default(null),
  weight: numericRangeSchema.nullable().default(null),
  duration: z
    .object({
      format: z.enum(["seconds", "minutes"]),
      value: numericRangeSchema.nullable(),
    })
    .nullable()
    .default(null),
  restTime: z.number().int().min(0).max(3600).nullable().default(null),
  tempo: z.union([z.string(), z.number()]).nullable().default(null),
  rir: z.number().int().min(-1).max(10).optional(),
  comment: z.string().max(2000).default(""),
  groupId: z.string().max(80).nullable().default(null),
  groupType: z.enum(["superset", "circuit"]).nullable().default(null),
});

export const trainingProgramDraftWorkoutSchema = z.object({
  workoutKey: z.string().min(1).max(64),
  name: z.string().min(1).max(160),
  description: z.string().max(4000).default(""),
  preWorkout: z.array(trainingProgramDraftExerciseSchema).max(20).default([]),
  exercises: z.array(trainingProgramDraftExerciseSchema).min(1).max(40),
  postWorkout: z.array(trainingProgramDraftExerciseSchema).max(20).default([]),
});

const draftProgramDaySchema = z.object({ workoutDay: z.string().min(1).nullable() });

export const trainingProgramDraftWeekSchema = z.object({
  monday: draftProgramDaySchema,
  tuesday: draftProgramDaySchema,
  wednesday: draftProgramDaySchema,
  thursday: draftProgramDaySchema,
  friday: draftProgramDaySchema,
  saturday: draftProgramDaySchema,
  sunday: draftProgramDaySchema,
});

export const trainingProgramDraftSchema = z
  .object({
    name: z.string().min(3).max(200),
    level: z.enum(["BEGINNER", "INTERMEDIATE", "ADVANCED"]),
    type: z.enum(["5X5", "FULL_BODY_WORKOUT", "AB_ROUTINE", "SPLIT", "PUSH_PULL", "BETTER_SHAPE", "FITNESS", "OTHER"]),
    tags: z.array(z.string().max(100)).max(30).default([]),
    comment: z.string().max(5000).default(""),
    workouts: z.array(trainingProgramDraftWorkoutSchema).min(1).max(14),
    defaultSchedule: z.array(trainingProgramDraftWeekSchema).min(1).max(2).nullable().default(null),
    notes: z.array(z.string().max(1000)).max(30).default([]),
  })
  .superRefine((draft, ctx) => {
    const workoutKeys = new Set<string>();
    draft.workouts.forEach((workout, index) => {
      if (workoutKeys.has(workout.workoutKey)) {
        ctx.addIssue({
          code: "custom",
          message: "workoutKey musi być unikalny",
          path: ["workouts", index, "workoutKey"],
        });
      }
      workoutKeys.add(workout.workoutKey);
    });

    draft.defaultSchedule?.forEach((week, weekIndex) => {
      Object.entries(week).forEach(([day, value]) => {
        if (value.workoutDay && !workoutKeys.has(value.workoutDay)) {
          ctx.addIssue({
            code: "custom",
            message: `Harmonogram wskazuje nieznany workoutKey: ${value.workoutDay}`,
            path: ["defaultSchedule", weekIndex, day, "workoutDay"],
          });
        }
      });
    });
  });

export const trainingProgramExerciseSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  bodyParts: z.array(z.string()),
  tags: z.array(z.string()),
  thumbnailUrl: z.string().nullable(),
});

export const trainingProgramProposalSchema = z.object({
  status: z.literal("proposed"),
  draft: trainingProgramDraftSchema,
  resolvedExercises: z.array(trainingProgramExerciseSummarySchema),
  issues: z.array(z.string()),
  canSave: z.boolean(),
});

export const trainingExerciseDraftSchema = z.object({
  name: z.string().trim().min(3).max(160),
  type: trainingExerciseTypeSchema,
  bodyParts: z.array(trainingExerciseBodyPartSchema).min(1).max(6),
  tags: z.array(z.string().max(100)).max(10).default([]),
  description: z.string().max(4000).default(""),
  instruction: z.array(z.string().max(500)).max(20).default([]),
  videoUrl: z.string().trim().url().max(500).nullable().default(null),
});

export const trainingExerciseVideoSchema = z.object({
  url: z.string(),
  videoId: z.string(),
  title: z.string(),
  thumbnailUrl: z.string(),
});

export const trainingExerciseProposalSchema = z.object({
  status: z.literal("exercise-proposed"),
  draft: trainingExerciseDraftSchema,
  video: trainingExerciseVideoSchema.nullable(),
  similarExercises: z.array(trainingProgramExerciseSummarySchema),
  issues: z.array(z.string()),
  canSave: z.boolean(),
});

export type TrainingProgramDraft = z.infer<typeof trainingProgramDraftSchema>;
export type TrainingProgramDraftExercise = z.infer<typeof trainingProgramDraftExerciseSchema>;
export type TrainingProgramProposal = z.infer<typeof trainingProgramProposalSchema>;
export type TrainingExerciseDraft = z.infer<typeof trainingExerciseDraftSchema>;
export type TrainingExerciseVideo = z.infer<typeof trainingExerciseVideoSchema>;
export type TrainingExerciseProposal = z.infer<typeof trainingExerciseProposalSchema>;
